import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { EquipmentTypeDto, MiscLineItemDto, QuoteDto, RegionDto, SiteDto } from '../../../../../dtos';
import { countries, emptyGuid } from '../../../../../util';
import { useGetRegionQuery } from '../../../../../store/generated/generatedApi';
import {
    IFormFieldValidationConfig,
    IFormProps,
    isGreaterThanOrEqualTo,
    isNotBlank,
    isShorterThanMaxLength,
    runFieldValidation,
    runFormValidation,
} from '../../../../CoreLib/library';

export interface IMiscLineItemFormValues {
    quoteId: string;
    equipmentTypeId: string;
    siteId?: string;
    cost: number;
    rate: number;
    quantity?: number;
    description: string;
    lineItemMemo?: string;
    driverMemo?: string;
    unitOfMeasure?: string;
    isActive: boolean;
    customerJobNumber?: string;
    poNumber?: string;
    isCustomPoNumber: boolean;
    isCustomerJobNumberCustom: boolean;
    haulToAddressLine1?: string;
    haulToAddressLine2?: string;
    haulToCity?: string;
    haulToState?: string;
    haulToZipCode?: string;
    haulToCountry?: string;
    haulToDirections?: string;
    haulToSiteName?: string;
    haulToRegion?: RegionDto;
}

export const DEFAULT_MISC_ITEM: MiscLineItemDto = {
    id: emptyGuid,
    quoteId: '',
    equipmentTypeId: emptyGuid,
    cost: 0,
    rate: 0,
    quantity: 0,
    description: '',
    lineItemMemo: '',
    driverMemo: '',
    unitOfMeasure: '',
    isActive: true,
    createdOn: new Date(),
    customerJobNumber: '',
    poNumber: '',
    isCustomPoNumber: false,
    isCustomerJobNumberCustom: false,
};

export function useMiscLineItemForm(props: IFormProps<MiscLineItemDto>, quote: QuoteDto | undefined) {
    const { save, cancel, initValues } = props;
    const { data: regions } = useGetRegionQuery({ searchText: '', sorting: ['NAME+'], page: 0, pageSize: 100000, includeInactive: false });
    const [formQuote, setFormQuote] = useState<QuoteDto>();
    const [formEquipmentType, setFormEquipmentType] = useState<EquipmentTypeDto>();
    const [formSite, setFormSite] = useState(initValues?.site);
    const [formSiteName, setFormSiteName] = useState(
        initValues?.site ? (initValues.site.name ?? '') : (initValues?.haulToSiteName ?? '')
    );
    const [formCost, setFormCost] = useState<number>();
    const [formRate, setFormRate] = useState<number>();
    const [formQuantity, setFormQuantity] = useState<number | undefined>(initValues?.quantity);
    const [formDescription, setFormDescription] = useState('');
    const [formLineItemMemo, setFormLineItemMemo] = useState<string | undefined>(initValues?.lineItemMemo);
    const [formDriverMemo, setFormDriverMemo] = useState<string | undefined>(initValues?.driverMemo);
    const [formUnitOfMeasure, setFormUnitOfMeasure] = useState<string>(initValues?.unitOfMeasure ?? '');
    const [formIsActive, setFormIsActive] = useState<boolean>(initValues?.isActive ?? true);
    const [formAddressLine1, setFormAddressLine1] = useState(initValues?.haulToAddressLine1 ?? initValues?.site?.addressLine1 ?? '');
    const [formAddressLine2, setFormAddressLine2] = useState(initValues?.haulToAddressLine2 ?? initValues?.site?.addressLine2 ?? '');
    const [formCountry, setFormCountry] = useState(initValues?.haulToCountry ?? initValues?.site?.country ?? countries[2]);
    const [formDirections, setFormDirections] = useState(initValues?.haulToDirections ?? initValues?.site?.directions ?? '');
    const [formCity, setFormCity] = useState(initValues?.haulToCity ?? initValues?.site?.city ?? '');
    const [formState, setFormState] = useState(initValues?.haulToState ?? initValues?.site?.state ?? 'IN');
    const [formZipCode, setFormZipCode] = useState(initValues?.haulToZipCode ?? initValues?.site?.zipCode ?? '');
    const [formRegion, setFormRegion] = useState<RegionDto | undefined>(initValues?.haulToRegion ?? initValues?.site?.region);
    const [formCustomerJobNumber, setFormCustomerJobNumber] = useState<string | undefined>(
        initValues?.isCustomerJobNumberCustom ? (initValues?.customerJobNumber === '' ? quote?.customerJobNumber ?? '' : initValues?.customerJobNumber ?? quote?.customerJobNumber ?? '') : quote?.customerJobNumber ?? ''
    );
    const [formPoNumber, setFormPoNumber] = useState<string | undefined>(
        initValues?.isCustomPoNumber ? (initValues?.poNumber === '' ? quote?.poNumber ?? '' : initValues?.poNumber ?? quote?.poNumber ?? '') : quote?.poNumber ?? ''
    );

    const [formQuotePoNumber, setFormQuotePoNumber] = useState<string | undefined>(quote?.poNumber);
    const [formQuoteCustomerJobNumber, setFormQuoteCustomerJobNumber] = useState<string | undefined>(quote?.customerJobNumber);
    const [formIsCustomPoNumber, setFormIsCustomPoNumber] = useState<boolean>(initValues?.isCustomPoNumber ?? false);
    const [formIsCustomerJobNumberCustom, setFormIsCustomerJobNumberCustom] = useState<boolean>(initValues?.isCustomerJobNumberCustom ?? false);

    useEffect(() => {
        setFormQuote(initValues?.quote);
        setFormEquipmentType(initValues?.equipmentType);
        setFormSite(initValues?.site);
        setFormSiteName(initValues?.site ? (initValues.site.name ?? '') : (initValues?.haulToSiteName ?? ''));
        setFormCost(initValues?.cost ?? 0);
        setFormRate(initValues?.rate ?? 0);
        setFormQuantity(initValues?.quantity);
        setFormDescription(initValues?.description ?? '');
        setFormIsActive(initValues?.isActive ?? true);
        setFormAddressLine1(initValues?.haulToAddressLine1 ?? initValues?.site?.addressLine1 ?? '');
        setFormAddressLine2(initValues?.haulToAddressLine2 ?? initValues?.site?.addressLine2 ?? '');
        setFormCountry(initValues?.haulToCountry ?? initValues?.site?.country ?? countries[2]);
        setFormDirections(initValues?.haulToDirections ?? initValues?.site?.directions ?? '');
        setFormCity(initValues?.haulToCity ?? initValues?.site?.city ?? '');
        setFormState(initValues?.haulToState ?? initValues?.site?.state ?? 'IN');
        setFormZipCode(initValues?.haulToZipCode ?? initValues?.site?.zipCode ?? '');
        setFormRegion(initValues?.haulToRegion ?? initValues?.site?.region);
        setFormQuotePoNumber(quote?.poNumber);
        setFormQuoteCustomerJobNumber(quote?.customerJobNumber);
        setFormPoNumber(initValues?.isCustomPoNumber ? (initValues?.poNumber === '' ? quote?.poNumber ?? '' : initValues?.poNumber ?? quote?.poNumber ?? '') : quote?.poNumber ?? '');
        setFormCustomerJobNumber(initValues?.isCustomerJobNumberCustom ? (initValues?.customerJobNumber === '' ? quote?.customerJobNumber ?? '' : initValues?.customerJobNumber ?? quote?.customerJobNumber ?? '') : quote?.customerJobNumber ?? '');
    }, [initValues, quote?.poNumber, quote?.customerJobNumber]);

    const [fieldErrors, setFieldErrors] = useState<Map<keyof IMiscLineItemFormValues, string>>(
        new Map([
            ['quoteId', ''],
            ['equipmentTypeId', ''],
            ['siteId', ''],
            ['cost', ''],
            ['rate', ''],
            ['quantity', ''],
            ['description', ''],
            ['lineItemMemo', ''],
            ['unitOfMeasure', ''],
            ['customerJobNumber', ''],
            ['poNumber', ''],
        ])
    );

    const formFieldValidators = useMemo(
        () =>
            new Map<keyof IMiscLineItemFormValues, IFormFieldValidationConfig>([
                [
                    'quoteId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Quote',
                    },
                ],
                [
                    'equipmentTypeId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Equipment Type',
                    },
                ],
                [
                    'cost',
                    {
                        validators: [isNotBlank, isGreaterThanOrEqualTo(0)],
                        errorMessageEntityName: 'Cost',
                    },
                ],
                [
                    'rate',
                    {
                        validators: [isNotBlank, isGreaterThanOrEqualTo(0)],
                        errorMessageEntityName: 'Rate',
                    },
                ],
                [
                    'quantity',
                    {
                        validators: [isGreaterThanOrEqualTo(0)],
                        errorMessageEntityName: 'Quantity',
                    },
                ],
                [
                    'description',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Description',
                    },
                ],
                [
                    'unitOfMeasure',
                    {
                        validators: [isShorterThanMaxLength(20)],
                        errorMessageEntityName: 'Unit Of Measure',
                    },
                ],
                [
                    'poNumber',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'PO Number',
                    },
                ],
            ]),
        []
    );

    const getCurrentFormValues = useCallback((): IMiscLineItemFormValues => {
        return {
            quoteId: formQuote?.id ?? emptyGuid,
            equipmentTypeId: formEquipmentType?.id ?? emptyGuid,
            siteId: formSite?.id,
            cost: formCost ?? 0,
            rate: formRate ?? 0,
            quantity: formQuantity,
            description: formDescription ?? '',
            lineItemMemo: formLineItemMemo ?? '',
            driverMemo: formDriverMemo ?? '',
            unitOfMeasure: formUnitOfMeasure ?? '',
            isActive: formIsActive,
            customerJobNumber: formCustomerJobNumber,
            poNumber: formPoNumber,
            isCustomPoNumber: formIsCustomPoNumber,
            isCustomerJobNumberCustom: formIsCustomerJobNumberCustom,
            haulToAddressLine1: formAddressLine1,
            haulToAddressLine2: formAddressLine2,
            haulToCity: formCity,
            haulToState: formState,
            haulToZipCode: formZipCode,
            haulToCountry: formCountry,
            haulToDirections: formDirections,
            haulToSiteName: formSite ? (formSite.name ?? '') : formSiteName,
            haulToRegion: formRegion,
        };
    }, [
        formCost,
        formCustomerJobNumber,
        formDescription,
        formEquipmentType?.id,
        formIsActive,
        formLineItemMemo,
        formDriverMemo,
        formPoNumber,
        formQuantity,
        formQuote?.id,
        formRate,
        formSite,
        formUnitOfMeasure,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formSiteName,
        formRegion,
    ]);

    const validateForm = useCallback(() => {
        const formValues = getCurrentFormValues();
        const validationResult = runFormValidation<Partial<IMiscLineItemFormValues>>(formValues, formFieldValidators);
        setFieldErrors(validationResult.errorMessages);
        return validationResult.isValid;
    }, [formFieldValidators, getCurrentFormValues]);

    const validateField = useCallback(
        (fieldName: keyof IMiscLineItemFormValues) => {
            const validationConfig = formFieldValidators.get(fieldName);
            if (validationConfig) {
                const formValues = getCurrentFormValues();
                const fieldValue = formValues[fieldName];
                const { errorMessage } = runFieldValidation(fieldValue, validationConfig);

                if (errorMessage !== fieldErrors.get(fieldName)) {
                    const updatedFieldErrors = _.cloneDeep(fieldErrors);
                    updatedFieldErrors.set(fieldName, errorMessage);
                    setFieldErrors(updatedFieldErrors);
                }
            }
        },
        [fieldErrors, formFieldValidators, getCurrentFormValues]
    );

    const isFormDirty = useCallback(() => {
        var isDirty = formIsActive !== initValues?.isActive;
        isDirty = isDirty || formQuote?.id !== initValues?.quote?.id;
        isDirty = isDirty || formEquipmentType?.id !== initValues?.equipmentType?.id;
        isDirty = isDirty || formSite?.id !== initValues?.site?.id;
        isDirty = isDirty || formRate !== initValues?.rate;
        isDirty = isDirty || formCost !== initValues?.cost;
        isDirty = isDirty || formQuantity !== initValues?.quantity;
        isDirty = isDirty || formDescription !== initValues?.description;
        isDirty = isDirty || formUnitOfMeasure !== initValues?.unitOfMeasure;
        isDirty = isDirty || formLineItemMemo !== initValues?.lineItemMemo;
        isDirty = isDirty || formDriverMemo !== initValues?.driverMemo;
        isDirty = isDirty || formCustomerJobNumber !== initValues?.customerJobNumber;
        isDirty = isDirty || formPoNumber !== initValues?.poNumber;
        isDirty = isDirty || formIsCustomPoNumber !== initValues?.isCustomPoNumber;
        isDirty = isDirty || formIsCustomerJobNumberCustom !== initValues?.isCustomerJobNumberCustom;
        isDirty = isDirty || formAddressLine1 !== initValues?.haulToAddressLine1;
        isDirty = isDirty || formAddressLine2 !== initValues?.haulToAddressLine2;
        isDirty = isDirty || formCity !== initValues?.haulToCity;
        isDirty = isDirty || formState !== initValues?.haulToState;
        isDirty = isDirty || formZipCode !== initValues?.haulToZipCode;
        isDirty = isDirty || formCountry !== initValues?.haulToCountry;
        isDirty = isDirty || formDirections !== initValues?.haulToDirections;
        isDirty = isDirty || formSiteName !== initValues?.haulToSiteName;
        isDirty = isDirty || formRegion !== initValues?.haulToRegion;

        return isDirty;
    }, [
        formCost,
        formCustomerJobNumber,
        formDescription,
        formEquipmentType?.id,
        formIsActive,
        formLineItemMemo,
        formDriverMemo,
        formPoNumber,
        formQuantity,
        formQuote?.id,
        formRate,
        formSite?.id,
        formUnitOfMeasure,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formSiteName,
        formRegion,
        initValues?.cost,
        initValues?.customerJobNumber,
        initValues?.description,
        initValues?.equipmentType?.id,
        initValues?.isActive,
        initValues?.lineItemMemo,
        initValues?.driverMemo,
        initValues?.poNumber,
        initValues?.quantity,
        initValues?.quote?.id,
        initValues?.rate,
        initValues?.site?.id,
        initValues?.unitOfMeasure,
        initValues?.isCustomPoNumber,
        initValues?.isCustomerJobNumberCustom,
        initValues?.haulToAddressLine1,
        initValues?.haulToAddressLine2,
        initValues?.haulToCity,
        initValues?.haulToState,
        initValues?.haulToZipCode,
        initValues?.haulToCountry,
        initValues?.haulToDirections,
        initValues?.haulToSiteName,
        initValues?.haulToRegion,
    ]);

    const handleQuoteChange = useCallback((quote: QuoteDto) => {
        setFormQuote(quote);
    }, []);

    const handleEquipmentTypeChange = useCallback((equipmentType?: EquipmentTypeDto) => {
        setFormEquipmentType(equipmentType);
    }, []);

    const handleFormAddressChange = useCallback((address) => {
        setFormAddressLine1(address.addressLine1 ?? '');
        setFormAddressLine2(address.addressLine2 ?? '');
        setFormCity(address.city ?? '');
        setFormState(address.state ?? '');
        setFormZipCode(address.zipCode ?? '');
        setFormCountry(address.country ?? countries[2]);
        setFormDirections(address.directions ?? '');
    }, []);

    const handleFormRegionChange = useCallback((region: RegionDto | undefined) => {
        setFormRegion(region);
    }, []);

    const handleSiteChange = useCallback((site?: SiteDto) => {
        setFormSite(site);
        setFormSiteName(site?.name ?? '');
        setFormAddressLine1(site?.addressLine1 ?? '');
        setFormAddressLine2(site?.addressLine2 ?? '');
        setFormCity(site?.city ?? '');
        setFormState(site?.state ?? 'IN');
        setFormZipCode(site?.zipCode ?? '');
        setFormCountry(site?.country ?? countries[2]);
        setFormDirections(site?.directions ?? '');
        setFormRegion(site?.region);
    }, []);

    const handleRateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormRate(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleCostChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormCost(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleQuantityChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormQuantity(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleDescriptionChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormDescription(event.target.value);
    }, []);

    const handleLineItemMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormLineItemMemo(event.target.value ?? '');
    }, []);

    const handleDriverMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormDriverMemo(event.target.value ?? '');
    }, []);

    const handleUnitOfMeasureChange = useCallback((value?: string) => {
        setFormUnitOfMeasure(value ?? '');
    }, []);

    const handleIsActiveChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsActive(isChecked);
    }, []);

    const handleCustomerJobNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormCustomerJobNumber(event.target.value ?? '');
    }, []);

    const handlePoNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormPoNumber(event.target.value ?? '');
    }, []);

    const handleSave = useCallback(() => {
        const isFormValid = validateForm();
        if (isFormValid) {
            const formValues = getCurrentFormValues();
            const updatedUserRecord = {
                ...(initValues ?? DEFAULT_MISC_ITEM),
                ...formValues,
            };
            save(updatedUserRecord);
        }
    }, [getCurrentFormValues, initValues, save, validateForm]);

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    const handleIsCustomPoNumberChange = useCallback((value: boolean) => {
        setFormIsCustomPoNumber(value);
        if(!value) {
            setFormPoNumber(formQuotePoNumber);
        }
    }, [formQuotePoNumber, setFormPoNumber]);

    const handleIsCustomerJobNumberCustomChange = useCallback((value: boolean) => {
        setFormIsCustomerJobNumberCustom(value);
        if(!value) {
            setFormCustomerJobNumber(formQuoteCustomerJobNumber);
        }
    }, [formQuoteCustomerJobNumber, setFormCustomerJobNumber]);

    const formTotal = useMemo(() => {
        return (formRate ?? 0) * (formQuantity ?? 0);
    }, [formRate, formQuantity]);

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleQuoteChange,
        handleEquipmentTypeChange,
        handleSiteChange,
        handleRateChange,
        handleCostChange,
        handleQuantityChange,
        handleDescriptionChange,
        handleLineItemMemoChange,
        handleDriverMemoChange,
        handleUnitOfMeasureChange,
        handleIsActiveChange,
        handleCustomerJobNumberChange,
        handlePoNumberChange,
        handleIsCustomPoNumberChange,
        handleIsCustomerJobNumberCustomChange,
        handleFormAddressChange,
        handleFormRegionChange,
        formQuote,
        formEquipmentType,
        formSite,
        formSiteName,
        formRate,
        formCost,
        formQuantity,
        formDescription,
        formLineItemMemo,
        formDriverMemo,
        formUnitOfMeasure,
        formIsActive,
        formCustomerJobNumber,
        formPoNumber,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formQuotePoNumber,
        formQuoteCustomerJobNumber,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formRegion,
        regions,
        validateField,
        initValues,
        formTotal,
    };
}
